import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from 'hooks';
import { selectAllId } from 'pages/tasks/constants';
import { FilterMatchMode } from 'primereact/api';
import {
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { useDebounce as debouncedState } from 'primereact/hooks';
import { TreeNode } from 'types/interfaces';
import { DropdownSelectorType, selectedDate } from 'types/interfaces';
import {   
  findNodeByKey,
  getAllKeys,
  TooltipPosition } from 'utils';

import { DROPDOWN_FILTER_DELAY } from 'constants/dropdownFilterDelay';
import pageSizeValues from 'ui/shared/paginator/pageSizeValues';

const withoutDepartmentsId = '1713383261709';

export interface UseTasksFiltersReturn {
  searchFilter: string;
  setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
  statusFilterType: 'all' | 'closed' | 'open';
  setStatusFilterType: React.Dispatch<React.SetStateAction<'all' | 'closed' | 'open'>>;
  filters: Record<string, DataTableFilterMetaData>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, DataTableFilterMetaData>>>;
  customFilters: { global: string };
  setCustomFilters: React.Dispatch<React.SetStateAction<{ global: string }>>;
  selectedTaskTypeFilter: DropdownSelectorType[];
  setSelectedTaskTypeFilter: React.Dispatch<React.SetStateAction<DropdownSelectorType[]>>;
  selectedStatusFilter: DropdownSelectorType[];
  setSelectedStatusFilter: React.Dispatch<React.SetStateAction<DropdownSelectorType[]>>;
  selectedPriorityFilter: DropdownSelectorType[];
  setSelectedPriorityFilter: React.Dispatch<React.SetStateAction<DropdownSelectorType[]>>;
  selectedDateFilter: selectedDate;
  setSelectedDateFilter: React.Dispatch<React.SetStateAction<selectedDate>>;
  selectedDepartmentsFilter: TreeNode[];
  setSelectedDepartmentsFilter: React.Dispatch<React.SetStateAction<TreeNode[]>>;
  treeItemToRemove: string | number;
  setTreeItemToRemove: React.Dispatch<React.SetStateAction<string | number>>;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  first: number;
  setFirst: React.Dispatch<React.SetStateAction<number>>;
  multiSortMeta: DataTableSortMeta[] | undefined | null;
  setMultiSortMeta: React.Dispatch<React.SetStateAction<DataTableSortMeta[] | undefined | null>>;
  
  filterRefs: {
    assetFilterRef: React.RefObject<HTMLInputElement>;
    actualStartDateFilterRef: React.RefObject<HTMLInputElement>;
    creationDateFilterRef: React.RefObject<HTMLInputElement>;
    taskNameFilterRef: React.RefObject<HTMLInputElement>;
    departmentNameFilterRef: React.RefObject<HTMLInputElement>;
    employeesFilterRef: React.RefObject<HTMLInputElement>;
    idFilterRef: React.RefObject<HTMLInputElement>;
    plannedStartDateFilterRef: React.RefObject<HTMLInputElement>;
    plannedEndDateFilterRef: React.RefObject<HTMLInputElement>;
    actualEndDateFilterRef: React.RefObject<HTMLInputElement>;
  };
  
  onFiltersChange: (e: DataTableStateEvent) => void;
  onFilterChange: (filter: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputChange: (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiselectFilterChange: (
    filterKey: string,
    selectedValues: DropdownSelectorType[],
    setFilterState: (arg0: DropdownSelectorType[]) => void
  ) => void;
  clearArrayFilter: (filter: string) => void;
  handleDateFilterChange: (selection: selectedDate) => void;
  handleCustomFilterChange: (filterName: string, value: string) => void;
  handleSelectionChange: (selection: TreeNode[] | null) => void;
  debouncedHandleSelectionChange: (selection: TreeNode[] | null) => void;
  handleDeleteTagItem: (taskId: string | number) => void;
  
  filtersForApi: any;
  
  isFirstRender: React.MutableRefObject<boolean>;
  departments: TreeNode[];
  setDepartments: (value: TreeNode[]) => void;
  isDepartmentsLoaded: boolean;
}

export const useTasksFilters = (
  departmentsParam: TreeNode[],
  isDepartmentsLoadedParam: boolean,
  setDepartmentsParam: (value: TreeNode[]) => void,
  onFilterChangeCallback?: () => void
): UseTasksFiltersReturn => {
  
  const [searchFilter, setSearchFilter] = useState<string>(
    localStorage.getItem('searchFilter') ?? 'creationDateBetween'
  );
  
  const [statusFilterType, setStatusFilterType] = useState<'all' | 'closed' | 'open'>('all');
  
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskType: { value: null, matchMode: FilterMatchMode.CUSTOM },
    taskStatus: { value: null, matchMode: FilterMatchMode.CUSTOM },
    assetName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    departmentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    employees: { value: null, matchMode: FilterMatchMode.CONTAINS },
    creationDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actualStartDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plannedStartDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plannedEndDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actualEndDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    closeCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
    priority: { value: '', matchMode: FilterMatchMode.CUSTOM }
  });
  
  const [customFilters, setCustomFilters] = useState({
    global: ''
  });
  
  const [selectedTaskTypeFilter, setSelectedTaskTypeFilter] = useState<
    DropdownSelectorType[]
  >([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    DropdownSelectorType[]
  >([]);
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<
    DropdownSelectorType[]
  >([]);
  
  const [
    selectedDepartmentsFilter,
    dbouncedSelectedDepartmentsFilter,
    setSelectedDepartmentsFilter
  ] = debouncedState<TreeNode[]>([], 700);
  
  const [selectedDateFilter, setSelectedDateFilter] = useState<selectedDate>(
    () => {
      const saved = localStorage.getItem('selectedDateFilter');
      return saved
        ? JSON.parse(saved)
        : {
          dateFrom: '',
          dateUntil: ''
        };
    }
  );
  
  const [idTooltipPosition, setIdTooltipPosition] =
    useState<TooltipPosition>('top');
  
  const [treeItemToRemove, setTreeItemToRemove] = useState<string | number>('');
  
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  
  const [first, setFirst] = useState(0);
  
  const isFirstRender = useRef(true);
  
  const assetFilterRef = useRef<HTMLInputElement>(null);
  const actualStartDateFilterRef = useRef<HTMLInputElement>(null);
  const creationDateFilterRef = useRef<HTMLInputElement>(null);
  const taskNameFilterRef = useRef<HTMLInputElement>(null);
  const departmentNameFilterRef = useRef<HTMLInputElement>(null);
  const employeesFilterRef = useRef<HTMLInputElement>(null);
  const idFilterRef = useRef<HTMLInputElement>(null);
  const plannedStartDateFilterRef = useRef<HTMLInputElement>(null);
  const plannedEndDateFilterRef = useRef<HTMLInputElement>(null);
  const actualEndDateFilterRef = useRef<HTMLInputElement>(null);
  
  const filtersForApi = useMemo(() => {
    const { taskType, taskStatus, priority, ...fixedFilters } = filters;
    const sort = multiSortMeta?.find((x) => x !== undefined);

    const isSelectAllSelected =
      findNodeByKey(selectedDepartmentsFilter, selectAllId) !== null;
    const isWithoutDepartmentsSelected =
      findNodeByKey(selectedDepartmentsFilter, withoutDepartmentsId) !== null;

    let isClosedValue = '';
    if (statusFilterType === 'closed') {
      isClosedValue = 'true';
    } else if (statusFilterType === 'open') {
      isClosedValue = 'false';
    }

    const departmentValue =
      selectedDepartmentsFilter.length === 0
        ? 'empty'
        : isSelectAllSelected
          ? isWithoutDepartmentsSelected
            ? ['', ...getAllKeys(departmentsParam)].join(',')
            : getAllKeys(departmentsParam).join(',')
          : selectedDepartmentsFilter
            .map((x: { key: string }) =>
              x.key === withoutDepartmentsId ? '' : x.key ?? ''
            )
            .join(',');

    return {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(
        Object.keys(fixedFilters)
          .map((x): any => {
            const f = fixedFilters[x];
            return {
              field: x,
              value: (f as DataTableFilterMetaData).value,
              operator: (f as DataTableFilterMetaData).matchMode
            };
          })
          .concat(
            Object.keys(customFilters).flatMap((x) => {
              const f = (customFilters as { [key: string]: string })[x];
              return x === 'departmentName'
                ? []
                : [{ field: x, value: f, operator: FilterMatchMode.CONTAINS }];
            })
          )
          .concat({
            field: 'department',
            value: departmentValue,
            operator: FilterMatchMode.IN
          })
          .concat({
            field: 'taskType',
            value: selectedTaskTypeFilter.map((x) => x.label).join(','),
            operator:
              selectedTaskTypeFilter.length > 0
                ? FilterMatchMode.IN
                : FilterMatchMode.CONTAINS
          })
          .concat({
            field: 'taskStatus',
            value: selectedStatusFilter.map((x) => x.label).join(','),
            operator:
              selectedStatusFilter.length > 0
                ? FilterMatchMode.IN
                : FilterMatchMode.CONTAINS
          })
          .concat({
            field: 'priority',
            value: selectedPriorityFilter.map((x) => x.label).join(','),
            operator:
              selectedPriorityFilter.length > 0
                ? FilterMatchMode.IN
                : FilterMatchMode.CONTAINS
          })
          .concat({
            field: searchFilter,
            value: selectedDateFilter.dateFrom
              ? new Date(parseInt(selectedDateFilter.dateFrom)).toISOString()
              : '',
            additionalValue: selectedDateFilter.dateUntil
              ? new Date(parseInt(selectedDateFilter.dateUntil)).toISOString()
              : '',
            operator: FilterMatchMode.BETWEEN
          })
          .concat({
            field: 'isClosed',
            value: isClosedValue,
            operator: FilterMatchMode.CONTAINS
          })
      )
    };
  }, [
    filters,
    multiSortMeta,
    pageNumber,
    pageSize,
    customFilters,
    selectedDepartmentsFilter,
    departmentsParam,
    selectedTaskTypeFilter,
    selectedStatusFilter,
    selectedPriorityFilter,
    selectedDateFilter,
    searchFilter,
    statusFilterType
  ]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = useCallback((
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!filters[filter]) {
      console.error(`Filter ${filter} is undefined`);
      return;
    }
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  }, [filters]);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange = useCallback(
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    },
    [debouncedOnFilterChange]
  );

  const handleMultiselectFilterChange = useCallback((
    filterKey: string,
    selectedValues: DropdownSelectorType[],
    setFilterState: (arg0: DropdownSelectorType[]) => void
  ) => {
    setFilterState(selectedValues);
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filterKey].value = selectedValues
      .map((item) => item.label)
      .join('');
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  }, [filters]);

  const handleDeleteTagItem = useCallback((taskId: string | number) => {
    setSelectedDepartmentsFilter((prevSelected) => {
      const updated = prevSelected.filter((item) => item.key !== String(taskId));

      localStorage.setItem(
        'storedDepartmentsFilter',
        JSON.stringify(updated)
      );
      
      return updated;
    });
    setTreeItemToRemove('');
  }, []);

  const handleFilterChange = useCallback((filterName: string, value: string) => {
    setPageNumber(1);
    setFirst(0);
    setCustomFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value
    }));
  }, []);

  const { debouncedCallback: debouncedOnCustomFilterChange } = useDebounce(
    handleFilterChange,
    300
  );

  const handleCustomFilterChange = useCallback((filterName: string, value: string) => {
    debouncedOnCustomFilterChange(filterName, value);
  }, [debouncedOnCustomFilterChange]);

  const handleSelectionChange = useCallback(
    (selection: TreeNode[] | null) => {
      if (!isDepartmentsLoadedParam) return;

      localStorage.setItem(
        'storedDepartmentsFilter',
        JSON.stringify(selection)
      );

      setPageNumber(1);
      setFirst(0);
      setSelectedDepartmentsFilter(selection as TreeNode[]);
    },
    [isDepartmentsLoadedParam]
  );

  const { debouncedCallback: debouncedHandleSelectionChange } = useDebounce(
    handleSelectionChange,
    DROPDOWN_FILTER_DELAY
  );

  const handleDateFilterChange = useCallback((selection: selectedDate) => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const isDateFromValid =
      selection.dateFrom !== null &&
      !isNaN(new Date(selection.dateFrom)?.getTime());
    const isDateUntilValid =
      selection.dateUntil !== null &&
      !isNaN(new Date(selection.dateUntil)?.getTime());
    if (isDateFromValid || isDateUntilValid) {
      const newDateFrom = isDateFromValid
        ? String(new Date(selection.dateFrom!)?.getTime())
        : '';
      const newDateUntil = isDateUntilValid
        ? String(new Date(selection.dateUntil!)?.getTime())
        : '';

      if (
        newDateFrom !== selectedDateFilter.dateFrom ||
        newDateUntil !== selectedDateFilter.dateUntil
      ) {
        setSelectedDateFilter({
          dateFrom: newDateFrom,
          dateUntil: newDateUntil
        });
        setPageNumber(1);
        setFirst(0);
      }
    } else {
      setSelectedDateFilter({ dateFrom: '', dateUntil: '' });
      setPageNumber(1);
      setFirst(0);
    }
  }, [selectedDateFilter]);

  const clearArrayFilter = useCallback((filter: string) => {
    const _filters = { ...filters } as Record<string, DataTableFilterMetaData>;
    _filters[filter].value = null;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);

    switch (filter) {
    case 'taskType':
      if (selectedTaskTypeFilter) setSelectedTaskTypeFilter([]);
      break;
    case 'taskStatus':
      if (selectedStatusFilter) setSelectedStatusFilter([]);
      break;
    case 'priority':
      if (selectedPriorityFilter) setSelectedPriorityFilter([]);
      break;
    default:
      break;
    }
  }, [filters, selectedTaskTypeFilter, selectedStatusFilter, selectedPriorityFilter]);

  useEffect(() => {
    localStorage.setItem('searchFilter', searchFilter);
  }, [searchFilter]);

  useEffect(() => {
    localStorage.setItem(
      'selectedDateFilter',
      JSON.stringify(selectedDateFilter)
    );
  }, [selectedDateFilter]);

  return {
    searchFilter,
    setSearchFilter,
    statusFilterType,
    setStatusFilterType,
    filters,
    setFilters,
    customFilters,
    setCustomFilters,
    selectedTaskTypeFilter,
    setSelectedTaskTypeFilter,
    selectedStatusFilter,
    setSelectedStatusFilter,
    selectedPriorityFilter,
    setSelectedPriorityFilter,
    selectedDateFilter,
    setSelectedDateFilter,
    selectedDepartmentsFilter,
    setSelectedDepartmentsFilter,
    treeItemToRemove,
    setTreeItemToRemove,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    first,
    setFirst,
    multiSortMeta,
    setMultiSortMeta,
    
    filterRefs: {
      assetFilterRef,
      actualStartDateFilterRef,
      creationDateFilterRef,
      taskNameFilterRef,
      departmentNameFilterRef,
      employeesFilterRef,
      idFilterRef,
      plannedStartDateFilterRef,
      plannedEndDateFilterRef,
      actualEndDateFilterRef
    },
    
    onFiltersChange,
    onFilterChange,
    handleInputChange,
    handleMultiselectFilterChange,
    clearArrayFilter,
    handleDateFilterChange,
    handleCustomFilterChange,
    handleSelectionChange,
    debouncedHandleSelectionChange,
    handleDeleteTagItem,
    
    filtersForApi,
    
    isFirstRender,
    departments: departmentsParam,
    setDepartments: setDepartmentsParam,
    isDepartmentsLoaded: isDepartmentsLoadedParam
  };
};